import { useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { Button, Input, Card, Badge } from '@/components/ui';
import { supabase } from '@/core/database/supabase';
import { PLAN_CONFIG } from '@/modules/admin/companies/types';
import type { SubscriptionPlanCode } from '@/modules/admin/companies/types';

type Step = 'company' | 'admin' | 'success';

export default function CompanyRegistrationPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const planCode = (searchParams.get('plan') || 'free') as SubscriptionPlanCode;
  const cycle = searchParams.get('cycle') || 'yearly';

  const [step, setStep] = useState<Step>('company');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    // Company
    companyName: '',
    companyCode: '',
    website: '',
    address: '',
    // Admin User
    fullName: '',
    email: '',
    password: '',
    phone: '',
  });

  const planCfg = PLAN_CONFIG[planCode] || PLAN_CONFIG.free;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError(null);
  };

  const handleNext = () => {
    if (step === 'company') {
      if (!formData.companyName || !formData.companyCode) {
        setError('Vui lòng nhập tên công ty và mã định danh');
        return;
      }
      setStep('admin');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.fullName || !formData.email || !formData.password) {
      setError('Vui lòng điền đầy đủ thông tin quản trị viên');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Create Auth User
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            full_name: formData.fullName,
            role_code: 'admin',
          }
        }
      });

      if (authError) throw authError;
      if (!authData.user) throw new Error('Không thể tạo tài khoản');

      // 2. Create Company (Inactive)
      const limits = {
        free: { projects: 1, users: 3, assets: 50 },
        basic: { projects: 5, users: 15, assets: 500 },
        pro: { projects: 20, users: 50, assets: 5000 },
        enterprise: { projects: -1, users: -1, assets: -1 },
      }[planCode] || { projects: 1, users: 3, assets: 50 };

      const { data: companyData, error: companyError } = await supabase.from('companies').insert({
        name: formData.companyName,
        code: formData.companyCode.toUpperCase(),
        website: formData.website,
        address: formData.address,
        subscription_plan_code: planCode,
        is_active: false, // Wait for approval
        owner_user_id: authData.user.id,
        max_projects: limits.projects,
        max_users: limits.users,
        max_assets: limits.assets,
      }).select().single();

      if (companyError) throw companyError;

      // 3. Update User Profile with company_id
      const { error: profileError } = await supabase.from('user_profiles').upsert({
        id: authData.user.id,
        company_id: companyData.id,
        full_name: formData.fullName,
        email: formData.email,
        phone: formData.phone,
        role_code: 'admin',
        is_active: false,
      });

      if (profileError) {
        console.error('Profile update error:', profileError);
        throw new Error('Đã tạo công ty nhưng không thể cập nhật thông tin người dùng: ' + profileError.message);
      }

      window.scrollTo({ top: 0, behavior: 'smooth' });
      setStep('success');
    } catch (err: any) {
      console.error('Registration error:', err);
      setError(err.message || 'Có lỗi xảy ra trong quá trình đăng ký. Vui lòng thử lại.');
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Left Panel: Info */}
      <div className="hidden lg:flex w-1/3 bg-slate-900 p-12 flex-col justify-between relative overflow-hidden">
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 mb-12">
            <div className="w-10 h-10 bg-emerald-500 rounded-xl flex items-center justify-center text-white">
              <i className="ri-leaf-line text-2xl" />
            </div>
            <span className="text-xl font-black text-white tracking-tight">Landscape</span>
          </Link>

          <div className="space-y-6">
            <Badge variant="custom" className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30 font-bold">
              Bước {step === 'company' ? '1' : step === 'admin' ? '2' : '3'}/2: {step === 'company' ? 'Thông tin tổ chức' : 'Quản trị viên'}
            </Badge>
            <h2 className="text-3xl font-black text-white leading-tight">
              Bắt đầu hành trình <br />chuyển đổi số cùng <span className="text-emerald-500">Landscape</span>
            </h2>
            <p className="text-slate-400">
              Chỉ mất vài phút để thiết lập không gian làm việc chuyên nghiệp cho đội ngũ của bạn.
            </p>
          </div>
        </div>

        {/* Plan Summary Card */}
        <div className="relative z-10 bg-white/5 border border-white/10 rounded-[32px] p-6 backdrop-blur-md">
          <p className="text-slate-500 text-[10px] font-black  tracking-widest mb-4">Gói đã chọn</p>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-black text-white">{planCfg.label} Edition</h3>
            <Badge variant="custom" className={`${planCfg.bg} ${planCfg.text} border-none font-black text-[10px]`}>
              {cycle.toUpperCase()}
            </Badge>
          </div>
          <div className="space-y-2 border-t border-white/10 pt-4">
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Thời hạn</span>
              <span className="text-white font-bold">{cycle === 'yearly' ? '12 tháng' : '1 tháng'}</span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-400">Trạng thái</span>
              <span className="text-amber-400 font-bold">Chờ kích hoạt</span>
            </div>
          </div>
        </div>

        <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/10 rounded-full -mr-32 -mt-32 blur-[80px]" />
      </div>

      {/* Right Panel: Form */}
      <div className="flex-1 p-6 md:p-12 flex items-center justify-center overflow-y-auto">
        <div className="w-full max-w-lg">
          {step === 'success' ? (
            <div className="text-center">
              <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 animate-bounce">
                <i className="ri-checkbox-circle-fill text-4xl" />
              </div>
              <h1 className="text-3xl font-black text-slate-900 mb-4">Đăng ký thành công!</h1>
              <p className="text-slate-500 mb-8 leading-relaxed">
                Yêu cầu đăng ký của công ty <strong>{formData.companyName}</strong> đã được gửi tới hệ thống quản trị.
                Chúng tôi sẽ thẩm định và kích hoạt tài khoản của bạn trong vòng 24 giờ làm việc.
              </p>
              <div className="bg-amber-50 border border-amber-100 rounded-2xl p-6 text-left mb-8">
                <h4 className="text-sm font-black text-amber-800 mb-2 flex items-center gap-2">
                  <i className="ri-information-line" /> Tiếp theo là gì?
                </h4>
                <ul className="text-xs text-amber-700 space-y-2">
                  <li>• Kiểm tra email xác nhận từ hệ thống.</li>
                  <li>• Đội ngũ hỗ trợ sẽ liên hệ nếu cần bổ sung thông tin.</li>
                  <li>• Sau khi kích hoạt, bạn có thể đăng nhập và bắt đầu thiết lập dự án.</li>
                </ul>
              </div>
              <Button variant="primary" className="w-full h-12 rounded-xl font-black  tracking-widest" onClick={() => navigate('/auth/login')}>
                Quay lại đăng nhập
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-8">
              <div>
                <h1 className="text-3xl font-black text-slate-900 mb-2">
                  {step === 'company' ? 'Thông tin tổ chức' : 'Thông tin Quản trị viên'}
                </h1>
                <p className="text-slate-500 text-sm">
                  {step === 'company' ? 'Vui lòng cung cấp thông tin cơ bản về công ty của bạn.' : 'Cung cấp thông tin người sẽ quản lý hệ thống này.'}
                </p>
              </div>

              {error && (
                <div className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-center gap-3 text-red-600 text-sm">
                  <i className="ri-error-warning-line" />
                  {error}
                </div>
              )}

              <div className="space-y-4">
                {step === 'company' ? (
                  <>
                    <Input label="Tên công ty / Tổ chức" name="companyName" value={formData.companyName} onChange={handleChange} placeholder="Công ty CP Cảnh quan Xanh" required />
                    <Input label="Mã định danh (Identifier)" name="companyCode" value={formData.companyCode} onChange={handleChange} placeholder="GREENLAND (Viết liền, không dấu)" required />
                    <Input label="Website" name="website" value={formData.website} onChange={handleChange} placeholder="https://company.com" />
                    <Input label="Địa chỉ trụ sở" name="address" value={formData.address} onChange={handleChange} placeholder="Số 1, Đường ABC, TP. HCM" />
                  </>
                ) : (
                  <>
                    <Input label="Họ và tên" name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Nguyễn Văn A" required />
                    <Input label="Email đăng nhập" name="email" type="email" value={formData.email} onChange={handleChange} placeholder="admin@company.com" required />
                    <Input label="Số điện thoại" name="phone" value={formData.phone} onChange={handleChange} placeholder="090 123 4567" />
                    <Input label="Mật khẩu" name="password" type="password" value={formData.password} onChange={handleChange} placeholder="Tối thiểu 8 ký tự" required />
                  </>
                )}
              </div>

              <div className="flex items-center gap-4 pt-4">
                {step === 'admin' && (
                  <Button variant="ghost" className="h-12 px-6 font-bold" onClick={() => setStep('company')}>
                    Quay lại
                  </Button>
                )}

                {step === 'company' ? (
                  <Button variant="primary" className="flex-1 h-12 rounded-xl font-black  tracking-widest" onClick={handleNext}>
                    Tiếp tục <i className="ri-arrow-right-line ml-2" />
                  </Button>
                ) : (
                  <Button variant="primary" className="flex-1 h-12 rounded-xl font-black  tracking-widest" type="submit" isLoading={isLoading}>
                    Hoàn tất đăng ký
                  </Button>
                )}
              </div>

              <p className="text-center text-xs text-slate-400">
                Bằng việc đăng ký, bạn đồng ý với <a href="#" className="text-emerald-600 hover:underline">Điều khoản dịch vụ</a> và <a href="#" className="text-emerald-600 hover:underline">Chính sách bảo mật</a> của Landscape.
              </p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
